import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Link2, Lock } from 'lucide-react';
import { Badge } from '@/shared/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import {
  declarerActif, getAgregateurs, getImpact, getRaccordementClient,
} from '@/shared/api/bridge';
import { libelleVerrou } from '@/features/bridge/bridge-etat';
import ConfirmationImpact from '@/features/bridge/components/ConfirmationImpact';

/**
 * Le raccordement des actifs d'un client à ses agrégateurs.
 *
 * POURQUOI CE BLOC EXISTE, ET POURQUOI IL EST SUR LA FICHE CLIENT
 *
 * Attribuer un client à un agrégateur n'ouvre plus rien depuis le 14/09/2026 : c'est la
 * DÉCLARATION de chaque actif qui le fait. Sans ce bloc, un exploitant attribue un
 * client et attend un revenu qui ne viendra jamais, sans aucun moyen de savoir qu'il
 * manque un geste.
 *
 * Le champ « agrégateur » de la fiche ne DÉCIDE plus, il PROPOSE : on s'en sert pour
 * présélectionner, l'utilisateur peut choisir autrement, actif par actif : le PV chez
 * Survoltage, la batterie chez Symphonics.
 *
 * UN CHOIX DÉFINITIF DEPUIS LE 07/10/2026
 *
 * Un actif déclaré y reste pour toujours : pas de « retirer », pas de second
 * agrégateur, pas de « déplacer ». Un actif déjà attribué affiche son agrégateur avec
 * un cadenas (même retiré, il lui reste attaché). Le choix n'est
 * proposé qu'aux actifs jamais déclarés, et le dialogue d'impact le dit avant le clic.
 */

const LIBELLE_MANDAT = {
  actif: 'mandat actif',
  non_signe: 'mandat non signé',
  signe: 'mandat signé',
};

export default function RaccordementAgregateurs({ clientId }) {
  const [etat, setEtat] = useState(null);
  const [agregateurs, setAgregateurs] = useState([]);
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const charger = useCallback(async () => {
    setErreur('');
    try {
      const [r, a] = await Promise.all([
        getRaccordementClient(clientId),
        getAgregateurs().catch(() => ({ agregateurs: [] })),
      ]);
      setEtat(r);
      setAgregateurs(a?.agregateurs || []);
    } catch (exc) {
      setErreur(exc?.response?.data?.detail || 'Raccordement illisible');
    }
  }, [clientId]);

  useEffect(() => { charger(); }, [charger]);

  // On DEMANDE l'impact avant d'écrire, jamais après. Un dialogue qui annoncerait la
  // perte une fois la déclaration posée ne servirait qu'à faire regretter.
  const demanderConfirmation = async (actif, agregateur) => {
    setEnCours(true);
    setErreur('');
    try {
      const impact = await getImpact(actif.asset_id, agregateur, false);
      setConfirmation({ actif, agregateur, impact });
    } catch (exc) {
      setErreur(exc?.response?.data?.detail || 'Impact incalculable');
    } finally {
      setEnCours(false);
    }
  };

  const appliquer = async () => {
    const { actif, agregateur } = confirmation;
    setEnCours(true);
    try {
      await declarerActif(agregateur, { asset_id: actif.asset_id });
      setConfirmation(null);
      await charger();
    } catch (exc) {
      setErreur(exc?.response?.data?.detail || 'Geste refusé');
    } finally {
      setEnCours(false);
    }
  };

  if (!etat) {
    return (
      <Card data-testid="client-raccordement">
        <CardContent className="pt-5 text-sm text-neutral-500">
          {erreur || 'Lecture du raccordement…'}
        </CardContent>
      </Card>
    );
  }

  const propose = etat.agregateur_propose;

  return (
    <Card data-testid="client-raccordement">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Link2 className="w-4 h-4" /> Raccordement aux agrégateurs
          {etat.a_declarer > 0 && (
            <Badge variant="outline" className="font-normal text-amber-700 border-amber-300">
              {etat.a_declarer} actif{etat.a_declarer > 1 ? 's' : ''} non déclaré
              {etat.a_declarer > 1 ? 's' : ''}
            </Badge>
          )}
        </CardTitle>
        <p className="text-[13px] text-neutral-500 leading-relaxed max-w-3xl">
          Un actif ne remonte à un agrégateur que s&apos;il lui est <strong>déclaré</strong>.
          {propose
            ? ` L'agrégateur de la fiche (${propose}) est proposé par défaut, vous pouvez
                en choisir un autre pour chaque équipement.`
            : " Aucun agrégateur n'est renseigné sur la fiche : choisissez-le équipement par équipement."}
          {' '}Ce choix est <strong>définitif</strong>.
        </p>
      </CardHeader>

      <CardContent className="space-y-3">
        {erreur && (
          <div className="rounded-md bg-red-50 text-red-700 px-3 py-2 text-[13px]"
               data-testid="raccordement-erreur">
            {erreur}
          </div>
        )}

        {etat.actifs.length === 0 && (
          <p className="text-[13px] text-neutral-500">
            Ce client ne porte aucun équipement.
          </p>
        )}

        {etat.actifs.map((actif) => (
          <div key={actif.asset_id}
               className="rounded-md border border-neutral-200 p-3 flex flex-wrap
                          items-center gap-3 justify-between"
               data-testid={`raccordement-actif-${actif.asset_id}`}>
            <div className="min-w-[220px]">
              <div className="text-[13px] font-medium text-neutral-800">
                {actif.type} {actif.marque ? `· ${actif.marque}` : ''}
                {actif.puissance_kw ? ` · ${actif.puissance_kw} kW` : ''}
              </div>
              <div className="text-[11px] text-neutral-500">
                {LIBELLE_MANDAT[actif.statut_mandat] || actif.statut_mandat}
                {!actif.materiel && ' · aucun identifiant matériel'}
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {actif.declare_a.map((d) => (
                <span key={d.agregateur}
                      className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border
                                 bg-emerald-50 text-emerald-700 border-emerald-200"
                      data-testid={`raccordement-declare-${actif.asset_id}`}>
                  <Lock className="w-3 h-3" aria-hidden /> {d.agregateur}
                </span>
              ))}

              {!actif.declare_a.length && actif.verrouille_chez && (
                <span className="flex items-center gap-1 text-[11px] text-neutral-500"
                      data-testid={`raccordement-verrou-${actif.asset_id}`}>
                  <Lock className="w-3 h-3" aria-hidden />
                  {libelleVerrou(actif.verrouille_chez)} (retiré)
                </span>
              )}

              {!actif.declarable && (
                <span className="text-[11px] text-amber-700 flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" /> {actif.blocage}
                </span>
              )}

              {actif.declarable && !actif.declare_a.length && !actif.verrouille_chez && (
                <select
                  className="h-8 rounded-md border border-neutral-200 px-2 text-[12px] bg-white"
                  disabled={enCours}
                  value=""
                  data-testid={`raccordement-choix-${actif.asset_id}`}
                  onChange={(e) => e.target.value
                    && demanderConfirmation(actif, e.target.value)}>
                  <option value="">déclarer à…</option>
                  {agregateurs.map((a) => (
                    <option key={a.cle} value={a.cle}>
                      {a.label}{a.cle === propose ? ' (proposé)' : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
        ))}
      </CardContent>

      {/* --- L'IMPACT, AVANT LE GESTE — partagé avec la page Bridge ------------ */}
      <ConfirmationImpact
        prefixe="raccordement"
        enCours={enCours}
        confirmation={confirmation && {
          ...confirmation,
          titre: `Déclarer · ${confirmation.actif.type} à ${confirmation.agregateur}`,
        }}
        onAnnuler={() => setConfirmation(null)}
        onConfirmer={appliquer} />
    </Card>
  );
}
